import { format, formatDistanceToNow, isValid, subHours } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { mapRejectionReasonCode } from '../dialogs/mapRejectionReasonCode'

import {
  RejectionReasonsCodeType,
  ValidationRequestLog,
  validationRequestLogTypes
} from '@percent/admin-dashboard/api/types'
import { LocaleKey } from '@percent/admin-dashboard/i18n/config'

type ValidationRequestLogData = {
  account?: string
  accountEmail?: string
  rejectionReasonCode?: RejectionReasonsCodeType
}

const isRejectionReasonCode = (value: unknown): value is RejectionReasonsCodeType =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(mapRejectionReasonCode, value)

export const useValidationRequestTimeline = (logs: ValidationRequestLog[] | null) => {
  const { t } = useTranslation()

  const getDescription = (log: ValidationRequestLog<ValidationRequestLogData>) => {
    const { accountEmail, rejectionReasonCode } = log.data ?? {}
    const author = typeof accountEmail === 'string' ? t('typography.by', { author: accountEmail }) : undefined
    const rejectionReason = isRejectionReasonCode(rejectionReasonCode)
      ? t(mapRejectionReasonCode[rejectionReasonCode] as LocaleKey)
      : undefined

    if (!author && !rejectionReason) return undefined

    return (
      <>
        {author && <div>{author}</div>}
        {rejectionReason && <div>{rejectionReason}</div>}
      </>
    )
  }

  const getTimestamp = (createdAt: string) => {
    const date = new Date(createdAt)

    if (!isValid(date)) return undefined

    return date < subHours(new Date(), 1)
      ? format(date, 'yyyy-MM-dd HH:mm')
      : formatDistanceToNow(date, { addSuffix: true })
  }

  if (!logs?.length) return null

  // pbp-api returns logs newest first. It owns the list of types, so one it adds before we do falls back to an unknown-status row.
  return logs.map(log => ({
    title: (validationRequestLogTypes as readonly string[]).includes(log.type)
      ? t(`typography.validationRequestTimeline.${log.type}`)
      : t('typography.timeline.unknownStatus'),
    description: getDescription(log),
    additionalDescription: getTimestamp(log.createdAt)
  }))
}
